
import React from 'react';
import { UserProfile, Client, Job } from '@/shared/types';
import { dataService } from '@/shared/services/dataService';

// Template Imports
import ClassicTemplate from '@/features/templates/components/ClassicTemplate';
import ModernTemplate from '@/features/templates/components/ModernTemplate';
import BoldTemplate from '@/features/templates/components/BoldTemplate';
import MinimalistTemplate from '@/features/templates/components/MinimalistTemplate';
import ExecutiveTemplate from '@/features/templates/components/ExecutiveTemplate';
import CreativeTemplate from '@/features/templates/components/CreativeTemplate';
import TechTemplate from '@/features/templates/components/TechTemplate';
import EcoTemplate from '@/features/templates/components/EcoTemplate';
import IndustrialTemplate from '@/features/templates/components/IndustrialTemplate';
import ElegantTemplate from '@/features/templates/components/ElegantTemplate';
import RetroTemplate from '@/features/templates/components/RetroTemplate';
import GeometricTemplate from '@/features/templates/components/GeometricTemplate';
import MagazineTemplate from '@/features/templates/components/MagazineTemplate';
import BlueprintTemplate from '@/features/templates/components/BlueprintTemplate';
import ArtisticTemplate from '@/features/templates/components/ArtisticTemplate';
import StartupTemplate from '@/features/templates/components/StartupTemplate';
import LegalTemplate from '@/features/templates/components/LegalTemplate';
import CyberTemplate from '@/features/templates/components/CyberTemplate';
import HandwrittenTemplate from '@/features/templates/components/HandwrittenTemplate';
import MonochromeTemplate from '@/features/templates/components/MonochromeTemplate';
import SystemInvoiceTemplate from '@/features/templates/components/SystemInvoiceTemplate';
import CorporateSuite from '@/features/templates/components/CorporateSuite/CorporateSuite';

interface PDFLayoutProps {
  profile: UserProfile;
  client: Client | null | undefined;
  title: string;
  metaData: { label: string; value: string }[];
  children: React.ReactNode;
  footerNote?: string;
  type: 'Invoice' | 'Quote' | 'Statement' | 'Rate Card' | 'Receipt';
  job?: Job; // Optional: Pass job to extract payment details
  hideHeader?: boolean; // Optional: Hide header section for content pages in multi-page documents
}

const PDFLayout: React.FC<PDFLayoutProps> = ({
  profile,
  client,
  title,
  metaData,
  children,
  footerNote,
  type,
  job,
  hideHeader = false
}) => {
  const { template, brandColor } = profile;
  const limits = dataService.getPlanLimits(profile.subscription.planId);
  // System templates never have watermarks
  const showWatermark = template !== 'system' && !limits.canRemoveBranding;

  // Construct a Display Profile favoring Business Details
  // Build it property by property to avoid exactOptionalPropertyTypes issues
  const displayProfile: UserProfile = {
      id: profile.id,
      userId: profile.userId || profile.id, // Alias for id for compatibility with service layer
      name: profile.businessName || profile.name,
      email: profile.businessEmail || profile.email,
      role: profile.role,
      brandColor: profile.brandColor,
      template: profile.template,
      subscription: profile.subscription,
      country: profile.country || 'US', // Use profile's country or default to US
      currency: profile.currency,
      savedServices: profile.savedServices,
      paymentMethods: profile.paymentMethods,
      joinedAt: profile.joinedAt,
      notificationPreferences: profile.notificationPreferences,
  };

  // Only add optional properties if they exist
  if (profile.password) {
    displayProfile.password = profile.password;
  }

  if (profile.businessName) {
    displayProfile.businessName = profile.businessName;
  }

  if (profile.businessAddress) {
    displayProfile.businessAddress = profile.businessAddress;
  }

  if (profile.businessEmail) {
    displayProfile.businessEmail = profile.businessEmail;
  }

  if (profile.businessPhone) {
    displayProfile.businessPhone = profile.businessPhone;
  }

  if (profile.logoUrl) {
    displayProfile.logoUrl = profile.logoUrl;
  }

  if (profile.address) {
    displayProfile.address = profile.address;
  }

  // phone is deprecated, but set it if we have a value
  if (profile.businessPhone || profile.phone) {
    const phoneValue = profile.businessPhone || profile.phone;
    if (phoneValue) {
      displayProfile.phone = phoneValue;
    }
  }

  if (profile.hasCompletedOnboarding) {
    displayProfile.hasCompletedOnboarding = profile.hasCompletedOnboarding;
  }

  // A4 dimensions: 794px x 1123px (approx at 96 DPI)
  const containerClass = "w-[794px] min-h-[1123px] bg-white relative text-neutral-900 font-sans mx-auto shadow-2xl overflow-hidden flex flex-col box-border";

  // Helper for logo styling to prevent distortion
  const LogoImage = ({ className, size = 'md', light = false }: { className?: string, size?: 'sm' | 'md' | 'lg' | 'xl', light?: boolean }) => {
    const sizeClasses = {
      sm: 'w-12 h-12',
      md: 'w-24 h-24',
      lg: 'w-32 h-32',
      xl: 'w-40 h-40'
    };

    return profile.logoUrl ? (
      <div className={`relative ${sizeClasses[size]} ${className} flex items-center justify-center shrink-0`}>
         <img 
          src={profile.logoUrl} 
          alt="Logo" 
          className="max-w-full max-h-full object-contain"
        />
      </div>
    ) : (
      <div 
        style={{ backgroundColor: light ? 'rgba(255,255,255,0.2)' : brandColor }} 
        className={`${sizeClasses[size]} ${className} ${light ? 'text-white' : 'text-white'} flex items-center justify-center font-bold text-3xl rounded-lg shadow-sm shrink-0`}
      >
        {displayProfile.name.charAt(0)}
      </div>
    );
  };

  const Watermark = () => (
    <div className="absolute inset-0 z-50 pointer-events-none flex items-center justify-center overflow-hidden">
        {/* Professional Centered Diagonal Watermark */}
        <div className="transform -rotate-45 opacity-[0.03] select-none pointer-events-none whitespace-nowrap">
             <span className="text-[120px] font-black text-neutral-900 uppercase tracking-widest border-4 border-neutral-900 px-12 py-4">
                 SwaySuite Free
             </span>
        </div>
        
        {/* Footer Strip */}
        <div className="absolute bottom-0 w-full bg-neutral-50 text-neutral-400 text-center py-1 text-[10px] font-medium uppercase tracking-widest border-t border-neutral-100">
           Generated with SwaySuite • Create your own invoices at swaysuite.com
        </div>
    </div>
  );
  
  // Payment Details Renderer
  const PaymentDetails = () => {
      // Find the specific method linked to the job, or fallback to default
      let method = null;
      if (job?.paymentMethodId) {
          method = profile.paymentMethods?.find(pm => pm.id === job.paymentMethodId);
      }
      
      // If no specific method linked or found, try finding a default
      if (!method && profile.paymentMethods?.length > 0) {
          method = profile.paymentMethods.find(pm => pm.isDefault) || profile.paymentMethods[0];
      }

      if (!method) return null;

      return (
          <div className="mt-8 pt-6 border-t border-neutral-100 bg-neutral-50/50 p-4 rounded-lg break-inside-avoid">
              <h4 className="font-bold text-neutral-800 text-sm mb-2 uppercase tracking-wide">Payment Instructions</h4>
              <div className="text-sm text-neutral-600">
                  <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">{method.name}</span>
                      <span className="text-xs px-2 py-0.5 bg-neutral-200 rounded uppercase">{method.type}</span>
                  </div>
                  <div className="whitespace-pre-wrap font-mono text-xs leading-relaxed opacity-90">
                      {method.details}
                  </div>
              </div>
          </div>
      );
  };

  // Wrap children to inject PaymentDetails before footer if it's an Invoice
  // (Not for System invoices, which handle their own content)
  const ContentWrapper = () => (
      <div className="flex flex-col h-full">
          <div className="flex-1">
            {children}
            {type === 'Invoice' && template !== 'system' && <PaymentDetails />}
          </div>
      </div>
  );

  const renderTemplate = () => {
      // Build template props carefully to handle exactOptionalPropertyTypes
      const baseProps = {
        profile: displayProfile,
        title,
        metaData,
        type,
        containerClass,
        LogoImage,
        hideHeader,
        children: <ContentWrapper />
      };

      // Create conditional props object for optional properties
      const optionalProps: {
        client?: Client | null;
        footerNote?: string;
      } = {};

      // Only add client if it's not null (undefined is fine for optional props)
      if (client !== null && client !== undefined) {
        optionalProps.client = client;
      }

      // Only add footerNote if it exists
      if (footerNote !== undefined) {
        optionalProps.footerNote = footerNote;
      }

      const templateProps = { ...baseProps, ...optionalProps };
      
      switch (template) {
          case 'system': return <SystemInvoiceTemplate {...templateProps} />;
          
          // Existing Collection
          case 'modern': return <ModernTemplate {...templateProps} />;
          case 'bold': return <BoldTemplate {...templateProps} />;
          case 'minimalist': return <MinimalistTemplate {...templateProps} />;
          case 'executive': return <ExecutiveTemplate {...templateProps} />;
          case 'creative': return <CreativeTemplate {...templateProps} />;
          case 'tech': return <TechTemplate {...templateProps} />;
          case 'eco': return <EcoTemplate {...templateProps} />;
          case 'industrial': return <IndustrialTemplate {...templateProps} />;
          case 'elegant': return <ElegantTemplate {...templateProps} />;
          case 'retro': return <RetroTemplate {...templateProps} />;
          case 'geometric': return <GeometricTemplate {...templateProps} />;
          case 'magazine': return <MagazineTemplate {...templateProps} />;
          case 'blueprint': return <BlueprintTemplate {...templateProps} />;
          case 'artistic': return <ArtisticTemplate {...templateProps} />;
          case 'startup': return <StartupTemplate {...templateProps} />;
          case 'legal': return <LegalTemplate {...templateProps} />;
          case 'cyber': return <CyberTemplate {...templateProps} />;
          case 'handwritten': return <HandwrittenTemplate {...templateProps} />;
          case 'monochrome': return <MonochromeTemplate {...templateProps} />;
          
          // New Corporate Suite Mappings
          case 'corp-alpha':
          case 'corp-beta':
          case 'corp-gamma':
          case 'corp-delta':
          case 'corp-epsilon':
          case 'corp-zeta':
          case 'corp-eta':
          case 'corp-theta':
          case 'corp-iota':
          case 'corp-kappa':
          case 'pro-slate':
          case 'pro-obsidian':
          case 'pro-ivory':
          case 'pro-navy':
          case 'finance-prime':
          case 'finance-elite':
          case 'consult-apex':
          case 'consult-zenith':
          case 'agency-vogue':
          case 'agency-aura':
              return <CorporateSuite variant={template} {...templateProps} />;

          case 'classic': default: return <ClassicTemplate {...templateProps} />;
      }
  };

  return (
      <div className="relative">
          {renderTemplate()}
          {showWatermark && <Watermark />}
      </div>
  );
};

export default PDFLayout;